<script setup>
import { ref } from 'vue';
import { useForm } from '@inertiajs/vue3';
import { Switch } from '@headlessui/vue';
import axios from 'axios';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';

const props = defineProps({
lot: Object,
conditionCategories: Array,
token: String
})

const lot = ref(props.lot)
const saveButton = ref(null);

const conditionsForm = useForm({
conditions: lot.value.conditions.map(condition => {
    return {
    answer: condition.answer == 1 ? true : false,
    comment: condition.comment,
    condition_category_id: condition.condition_category_id,
    condition_question_id: condition.condition_question_id,
    custom_question: condition.custom_question,
    existing_id: condition.id,
    enabled: condition.enabled == 1 ? true : false
    }
}),
images: [],
postalCode: lot.value.postal_code,
houseNumber: lot.value.house_number,
});

//add all condition categories to conditionsForm
props.conditionCategories.forEach(category => {
conditionsForm.conditions = conditionsForm.conditions.concat(category.questions.map(condition => {
    //check if condition_question_id already exists
    if (conditionsForm.conditions.find(formCondition => formCondition && formCondition.condition_question_id === condition.id)) return null;
    return {
    answer: false,
    comment: "",
    condition_category_id: condition.condition_category_id,
    condition_question_id: condition.id,
    custom_question: null,
    id: null,
    enabled: true
    }
}));
});

function getAnswer(questionId) {
var answer = lot.value.conditions.find(condition => condition.condition_question_id === questionId);

if (answer) {
    return answer;
}

return null;
}

function getConditionAnswerModel(questionId, custom) {
if (custom) {
    return conditionsForm.conditions.find(condition => condition !== null && condition.custom_question !== null && condition.existing_id === questionId).answer;
}
if (!conditionsForm.conditions.find(condition => condition !== null && condition.condition_question_id === questionId)) return null;
return conditionsForm.conditions.find(condition => condition !== null && condition.condition_question_id === questionId).answer;
}

function getConditionCommentModel(questionId, custom) {
if (custom) {
    return conditionsForm.conditions.find(condition => condition !== null && condition.custom_question !== null && condition.existing_id === questionId).comment;
}

if (!conditionsForm.conditions.find(condition => condition !== null && condition.condition_question_id === questionId)) return "";

return conditionsForm.conditions.find(condition => condition !== null && condition.condition_question_id === questionId).comment;
}

function setConditionCommentModel(questionId, custom, value) {
if (custom) {
    conditionsForm.conditions.find(condition => condition !== null && condition.custom_question !== null && condition.existing_id === questionId).comment = value.target.value;
} else {
    conditionsForm.conditions.find(condition => condition !== null && condition.condition_question_id === questionId).comment = value.target.value;
}
}

function setConditionAnswerModel(questionId, custom) {
if (custom) {
    conditionsForm.conditions.find(condition => condition !== null && condition.custom_question !== null && condition.existing_id === questionId).answer = !conditionsForm.conditions.find(condition => condition !== null && condition.custom_question !== null && condition.existing_id === questionId).answer;
} else {
    conditionsForm.conditions.find(condition => condition !== null && condition.condition_question_id === questionId).answer = !conditionsForm.conditions.find(condition => condition !== null && condition.condition_question_id === questionId).answer;
}
}


function onFileChange(event) {
const file = event.target.files;
conditionsForm.images = file;
}

const saveText = ref(null);

function saveConditions() {
//disable save button
saveButton.value.disabled = true;

//change saveText text to Aan het opslaan...
saveText.value.innerHTML = 'Aan het opslaan...';

//remove hidden class from element with id saveSpinner
document.getElementById('saveSpinner').classList.remove('hidden');

axios.post('/vragenlijst/' + lot.value.id + '/' + props.token + '/conditions', conditionsForm.data(), {
    headers: {
    'Content-Type': 'multipart/form-data'
    }
}).then(response => {
    if (response.data.success) {
    const successMessage = document.createElement('div');
    successMessage.innerHTML = '<div class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert"><strong class="font-bold">Gelukt!</strong><span class="block sm:inline"> De vragenlijst is succesvol opgeslagen. Je kunt de vragenlijst niet nogmaals invullen.</span></div>';
    saveButton.value.parentElement.appendChild(successMessage);

    //hide save button
    saveButton.value.style.display = 'none';
    } else {
    const errorMessage = document.createElement('div');
    errorMessage.innerHTML = '<div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert"><strong class="font-bold">Mislukt!</strong><span class="block sm:inline"> Er is iets misgegaan bij het opslaan van de vragenlijst.</span></div>';

    saveButton.value.parentElement.appendChild(errorMessage);

    //hide save button
    saveButton.value.style.display = 'none';
    }
}).catch(error => {
    if (error.response) {
    // if the error code is 422, we have validation errors
    if (error.response.status === 422) {
        conditionsForm.errors = Object.fromEntries(Object.entries(error.response.data.errors).map(([key, value]) => [key, value[0]]));

        //enable save button
        saveButton.value.disabled = false;

        //change saveText text to Opslaan
        saveText.value.innerHTML = 'Opslaan';

        //remove hidden class from element with id saveSpinner
        document.getElementById('saveSpinner').classList.add('hidden');

        return;
    }
    }
    const errorMessage = document.createElement('div');
    errorMessage.innerHTML = '<div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert"><strong class="font-bold">Mislukt!</strong><span class="block sm:inline"> Er is iets misgegaan bij het opslaan van de vragenlijst.</span></div>';

    saveButton.value.parentElement.appendChild(errorMessage);

    //hide save button
    saveButton.value.style.display = 'none';
});
}
</script>

<template>
    <div class="">
        <div class="h-full min-h-fit flex mt-12 bg-gradient-to-b from-transparent to-royalblue-500 via-lessbrightorange from-75% via-85%">
            <div class="flex flex-col mt-20 mb-10 mx-auto max-w-4xl gap-10">
                <div class="container mx-auto">
                    <div class="flex flex-col gap-2 mb-12">
                        <h1 class="text-4xl text-royalblue-500 text-balance">Hieronder vindt je een vragenlijst met standaardvragen die wij altijd stellen.</h1>
                        <p class="text-lg">De knop dient om 'ja' of 'nee' aan te geven. In het veld ernaast kun je eventueel een toelichting geven.</p>
                    </div>

                    <div v-for="category in props.conditionCategories" class="">
                        <div class="flex flex-col gap-2 bg-white rounded-2xl p-4 sm:p-6 md:p-8 shadow-xl shadow-deepblue/15 mb-8">
                            <p class="font-bold text-lg capitalize text-royalblue-500 mb-4">{{ category.name }}</p>
                            <div v-for="question in category.questions" :key="question.question" :set="answer = getAnswer(question.id)" class="grid grid-cols-2 gap-4">
                                <div class="flex flex-row gap-2 items-center">
                                    <p>{{ question.question }}</p>
                                </div>
                                <div class="flex flex-row gap-4 items-center">
                                    <Switch :v-model="getConditionAnswerModel(question.id, false)" @click="setConditionAnswerModel(question.id, false)"
                                        class="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-default items-center justify-center rounded-full hover:cursor-pointer">
                                        <span class="sr-only">Use setting</span>
                                        <span aria-hidden="true" class="pointer-events-none absolute h-full w-full rounded-md bg-white" />
                                        <span aria-hidden="true" :class="[getConditionAnswerModel(question.id, false) == true ? 'bg-royalblue-500' : 'bg-gray-200', 'pointer-events-none absolute  mx-auto h-6 w-12 rounded-full transition-colors duration-200 ease-in-out']" />
                                        <span aria-hidden="true" :class="[getConditionAnswerModel(question.id, false) == true ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out']" />
                                    </Switch>
                                    <div class="flex flex-row gap-0 w-full">
                                        <input class="input-style" :value="getConditionCommentModel(question.id, false)" @change="event => setConditionCommentModel(question.id, false, event)" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col gap-2 bg-white rounded-2xl p-4 sm:p-6 md:p-8 shadow-xl shadow-deepblue/15 mb-8">
                        <p class="font-bold text-lg capitalize text-royalblue-500 mb-4">Afbeeldingen</p>
                        <input type="file" multiple accept="image/*" v-on:change="onFileChange" class="flex border-none text-sm transition-colors file:mr-4 file:py-2 file:px-4 hover:cursor-pointer
                        file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-brightorange file:text-white file:hover:bg-brightorange/80 disabled:cursor-not-allowed disabled:opacity-50" placeholder="" />
                        <p v-if="conditionsForm.errors.images" class="text-red-500 text-sm pt-1">{{ conditionsForm.errors.images }}</p>
                    </div>

                    <div class="flex flex-col gap-2 bg-white rounded-2xl p-4 sm:p-6 md:p-8 shadow-xl shadow-deepblue/15 mb-8">
                        <p class="font-bold text-lg capitalize text-royalblue-500 mb-4">Locatie</p>
                        <div class="grid grid-cols-2 gap-4">
                            <div class="relative w-full">
                                <div class="flex flex-row justify-between items-center">
                                    <label for="name" class="block text-sm leading-6 text-gray-900">Postcode</label>
                                </div>
                                <div class="relative mt-2 rounded-m">
                                    <input id="postal_code" v-model="conditionsForm.postalCode" name="postalCode" class="input-style" />
                                    <div v-if="conditionsForm.errors.postalCode" class="pointer-events-none absolute top-0 bottom-0 right-0 flex items-center pr-3">
                                        <font-awesome-icon :icon="['fal', 'triangle-exclamation']" class="h-5 w-5 text-red-500 shrink-0" aria-hidden="true" />
                                    </div>
                                </div>
                                <p v-if="conditionsForm.errors.postalCode" class="text-red-500 text-sm pt-1">{{ conditionsForm.errors.postalCode }}</p>
                            </div>
                            <div class="relative w-full">
                                <div class="flex flex-row justify-between items-center">
                                    <label for="name" class="block text-sm leading-6 text-gray-900">Huisnummer</label>
                                    <Popover class="relative">
                                        <PopoverButton class="flex items-center">
                                            <font-awesome-icon :icon="['fal', 'circle-info']" class="h-4 w-4 text-royalblue-500 shrink-0" />
                                        </PopoverButton>

                                        <PopoverPanel class="absolute z-10 right-6 bottom-0 bg-white rounded-lg ring-1 ">
                                            <div class="w-64 flex flex-col p-2">
                                                <p class="text-sm text-gray-900">We gebruiken je postcode en huisnummer om een de locatie van je auto te bepalen om te helpen met ons verkoop proces.</p>
                                            </div>
                                            <img src="" alt="" />
                                        </PopoverPanel>
                                    </Popover>
                                </div>
                                <div class="relative mt-2 rounded-m">
                                    <input id="postal_code" v-model="conditionsForm.houseNumber" type="number" name="houseNumber" class="input-style" />
                                    <div v-if="conditionsForm.errors.houseNumber" class="pointer-events-none absolute top-0 bottom-0 right-0 flex items-center pr-3">
                                        <font-awesome-icon :icon="['fal', 'triangle-exclamation']" class="h-5 w-5 text-red-500 shrink-0" aria-hidden="true" />
                                    </div>
                                </div>
                                <p v-if="conditionsForm.errors.houseNumber" class="text-red-500 text-sm pt-1">{{ conditionsForm.errors.houseNumber }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="flex justify-center mt-4 mb-4">
                        <button ref="saveButton" @click="saveConditions" class="primary-btn sm:w-full">
                            <font-awesome-icon id="saveSpinner" class="hidden animate-spin" :icon="['far', 'spinner']" />
                            <span ref="saveText">Opslaan</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>